import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import api from '../config/api';
import PageNavbar from './PageNavbar';
import { ROLE_ORDER, ROLE_META, POS_PIN_ROLES } from '../config/roles';
import { formatDateDisplay, formatDateTimeDisplay } from '../utils/formatDate';
import './Users.css';

const ORDER_CATEGORY_OPTIONS = [
  { value: 'Online Orders', label: 'Online order' },
  { value: 'Events', label: 'Events' },
  { value: 'Store-1', label: 'Store 1' },
  { value: 'Store-2', label: 'Store 2' }
];

const ROLES_NEED_OUTLETS = ['Cashier', 'Franchise Admin', 'Store Manager', 'Order Creator'];

function roleBadgeClass(role) {
  return `role-badge role-${String(role).toLowerCase().replace(/\s+/g, '-')}`;
}

const Users = () => {
  const [users, setUsers] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [posDevices, setPosDevices] = useState([]);
  const [posEnrollments, setPosEnrollments] = useState([]);
  const [posDeviceLoading, setPosDeviceLoading] = useState(false);
  const [posDeviceForm, setPosDeviceForm] = useState({
    storeId: '',
    deviceName: '',
    pin: '',
    confirmPin: ''
  });
  const [generatedPosLink, setGeneratedPosLink] = useState('');
  const [generatedPosPin, setGeneratedPosPin] = useState('');
  const [storeForm, setStoreForm] = useState({
    code: '',
    name: '',
    city: 'Hyderabad',
    address: '',
    phone: '',
    gstin: ''
  });
  const [storeSaving, setStoreSaving] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [storeEditForm, setStoreEditForm] = useState({
    name: '',
    city: '',
    address: '',
    phone: '',
    gstin: '',
    is_active: true
  });
  const [clearPosPhrase, setClearPosPhrase] = useState('');
  const [clearPosBusy, setClearPosBusy] = useState(false);
  const [adminWaPhone, setAdminWaPhone] = useState('');
  const [adminWaSource, setAdminWaSource] = useState('');
  const [adminWaSaving, setAdminWaSaving] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'Order Creator',
    first_name: '',
    last_name: '',
    phone: '',
    allowed_categories: [],
    store_ids: [],
    pos_pin: '',
    pos_pin_clear: false
  });

  useEffect(() => {
    fetchUsers();
    loadStores();
    loadPosDevices();
    loadAdminWhatsApp();
  }, []);

  const loadAdminWhatsApp = async () => {
    try {
      const { data } = await api.get('/settings/whatsapp');
      setAdminWaPhone(data.admin_whatsapp_phone || '');
      setAdminWaSource(data.source || '');
    } catch {
      setAdminWaPhone('');
      setAdminWaSource('');
    }
  };

  const saveAdminWhatsApp = async (event) => {
    event.preventDefault();
    setAdminWaSaving(true);
    try {
      const { data } = await api.put('/settings/whatsapp', {
        admin_whatsapp_phone: adminWaPhone.trim()
      });
      setAdminWaPhone(data.admin_whatsapp_phone || adminWaPhone);
      setAdminWaSource('saved');
      alert(data.message || 'Admin WhatsApp number saved');
    } catch (error) {
      alert(error.response?.data?.error || 'Could not save WhatsApp number');
    } finally {
      setAdminWaSaving(false);
    }
  };

  const loadStores = async () => {
    try {
      const { data } = await api.get('/stores/admin');
      setStores(data.stores || []);
    } catch {
      try {
        const { data } = await api.get('/stores');
        setStores(data.stores || []);
      } catch {
        setStores([]);
      }
    }
  };

  const loadPosDevices = async () => {
    setPosDeviceLoading(true);
    try {
      const { data } = await api.get('/pos-devices/admin');
      setPosDevices(data.devices || []);
      setPosEnrollments(data.pending || []);
    } catch (error) {
      console.error('Error loading POS devices:', error);
    } finally {
      setPosDeviceLoading(false);
    }
  };

  const createStore = async (event) => {
    event.preventDefault();
    setStoreSaving(true);
    try {
      await api.post('/stores', {
        ...storeForm,
        code: storeForm.code.trim().toUpperCase(),
        gstin: storeForm.gstin.trim().toUpperCase()
      });
      setStoreForm({ code: '', name: '', city: 'Hyderabad', address: '', phone: '', gstin: '' });
      await loadStores();
      alert('Store created successfully');
    } catch (error) {
      alert(error.response?.data?.error || 'Could not create store');
    } finally {
      setStoreSaving(false);
    }
  };

  const openStoreEdit = (store) => {
    setEditingStore(store);
    setStoreEditForm({
      name: store.name || '',
      city: store.city || '',
      address: store.address || '',
      phone: store.phone || '',
      gstin: store.gstin || '',
      is_active: Boolean(store.is_active)
    });
  };

  const closeStoreEdit = () => {
    setEditingStore(null);
  };

  const saveStoreEdit = async (event) => {
    event.preventDefault();
    if (!editingStore) return;
    setStoreSaving(true);
    try {
      await api.patch(`/stores/${editingStore.id}`, {
        ...storeEditForm,
        gstin: storeEditForm.gstin.trim().toUpperCase()
      });
      closeStoreEdit();
      await loadStores();
      alert('Store updated');
    } catch (error) {
      alert(error.response?.data?.error || 'Could not update store');
    } finally {
      setStoreSaving(false);
    }
  };

  const deactivateStore = async (store) => {
    if (!window.confirm(`Archive "${store.name}"? It will be hidden from POS pickers.`)) return;
    try {
      await api.delete(`/stores/${store.id}`);
      await loadStores();
      alert('Store archived');
    } catch (error) {
      alert(error.response?.data?.error || 'Could not archive store');
    }
  };

  const permanentlyDeleteStore = async (store) => {
    const typed = window.prompt(
      `Permanently delete "${store.name}"?\nThis also removes that store's POS menu.\nType the store code ${store.code} to confirm.`
    );
    if (typed == null) return;
    if (String(typed).trim().toUpperCase() !== String(store.code).trim().toUpperCase()) {
      alert('Store code did not match. Delete cancelled.');
      return;
    }
    try {
      await api.delete(`/stores/${store.id}?permanent=1`);
      await loadStores();
      alert('Store deleted permanently');
    } catch (error) {
      alert(error.response?.data?.error || 'Could not delete store');
    }
  };

  const reactivateStore = async (store) => {
    try {
      await api.patch(`/stores/${store.id}`, { is_active: true });
      await loadStores();
      alert(`${store.name} is active again`);
    } catch (error) {
      alert(error.response?.data?.error || 'Could not reactivate store');
    }
  };

  const clearAllPosData = async () => {
    if (clearPosPhrase.trim() !== 'CLEAR POS SALES') {
      alert('Type CLEAR POS SALES exactly to confirm');
      return;
    }
    if (
      !window.confirm(
        'Delete ALL POS sales, sale lines, POS expenses, and receipt print jobs? This cannot be undone.'
      )
    ) {
      return;
    }
    setClearPosBusy(true);
    try {
      const { data } = await api.post('/stores/admin/clear-pos-data', {
        confirmPhrase: clearPosPhrase.trim()
      });
      setClearPosPhrase('');
      await loadStores();
      alert(
        `POS data cleared: ${data.deleted?.sales || 0} sales, ${data.deleted?.expenses || 0} expenses, ${data.deleted?.printJobs || 0} print jobs`
      );
    } catch (error) {
      alert(error.response?.data?.error || 'Could not clear POS data');
    } finally {
      setClearPosBusy(false);
    }
  };

  const generatePosEnrollment = async (event) => {
    event.preventDefault();
    if (!/^\d{4}$/.test(posDeviceForm.pin)) {
      alert('POS PIN must be exactly 4 digits');
      return;
    }
    if (posDeviceForm.pin !== posDeviceForm.confirmPin) {
      alert('POS PIN confirmation does not match');
      return;
    }
    try {
      const { data } = await api.post('/pos-devices/admin/enrollments', {
        storeId: Number(posDeviceForm.storeId),
        deviceName: posDeviceForm.deviceName.trim(),
        pin: posDeviceForm.pin
      });
      setGeneratedPosLink(data.enrollment.url);
      setGeneratedPosPin(posDeviceForm.pin);
      setPosDeviceForm((form) => ({ ...form, pin: '', confirmPin: '' }));
      await loadPosDevices();
    } catch (error) {
      alert(error.response?.data?.error || 'Could not generate POS enrollment link');
    }
  };

  const copyPosEnrollment = async () => {
    if (!generatedPosLink) return;
    try {
      await navigator.clipboard.writeText(generatedPosLink);
      alert('Reusable POS link copied');
    } catch {
      window.prompt('Copy this reusable POS link:', generatedPosLink);
    }
  };

  const revokePosDevice = async (id) => {
    if (!window.confirm('Revoke this POS desktop? It will immediately lose POS access.')) return;
    try {
      await api.post(`/pos-devices/admin/devices/${id}/revoke`);
      await loadPosDevices();
    } catch (error) {
      alert(error.response?.data?.error || 'Could not revoke POS device');
    }
  };

  const revokePosEnrollment = async (id) => {
    if (!window.confirm('Revoke this POS link and every browser opened from it?')) return;
    try {
      await api.post(`/pos-devices/admin/enrollments/${id}/revoke`);
      if (generatedPosLink) setGeneratedPosLink('');
      setGeneratedPosPin('');
      await loadPosDevices();
    } catch (error) {
      alert(error.response?.data?.error || 'Could not revoke enrollment link');
    }
  };

  const resetPosEnrollmentPin = async (id, storeName) => {
    const pin = window.prompt(`Enter a new 4-digit POS PIN for ${storeName || 'this link'}:`);
    if (pin == null) return;
    const nextPin = String(pin).trim();
    if (!/^\d{4}$/.test(nextPin)) {
      alert('POS PIN must be exactly 4 digits');
      return;
    }
    const confirmPin = window.prompt('Confirm the new 4-digit POS PIN:');
    if (confirmPin == null) return;
    if (String(confirmPin).trim() !== nextPin) {
      alert('POS PIN confirmation does not match');
      return;
    }
    try {
      await api.post(`/pos-devices/admin/enrollments/${id}/pin`, { pin: nextPin });
      setGeneratedPosPin(nextPin);
      alert(`POS PIN updated. New PIN: ${nextPin}`);
    } catch (error) {
      alert(error.response?.data?.error || 'Could not update POS PIN');
    }
  };

  useEffect(() => {
    if (showForm) loadStores();
  }, [showForm]);

  useEffect(() => {
    if (!showForm) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [showForm]);

  useEffect(() => {
    if (!showForm) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') resetForm();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showForm]);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data.users);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      password: '',
      role: 'Order Creator',
      first_name: '',
      last_name: '',
      phone: '',
      allowed_categories: [],
      store_ids: [],
      pos_pin: '',
      pos_pin_clear: false
    });
    setEditingUser(null);
    setShowForm(false);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      password: '',
      role: user.role,
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      phone: user.phone || '',
      allowed_categories: Array.isArray(user.allowed_categories) ? [...user.allowed_categories] : [],
      store_ids: Array.isArray(user.assigned_stores) ? user.assigned_stores.map((s) => s.id) : [],
      pos_pin: '',
      pos_pin_clear: false
    });
    setShowForm(true);
  };

  const toggleCategory = (value) => {
    const current = formData.allowed_categories || [];
    if (current.includes(value)) {
      setFormData({ ...formData, allowed_categories: current.filter((c) => c !== value) });
    } else {
      setFormData({ ...formData, allowed_categories: [...current, value] });
    }
  };

  const toggleStore = (id) => {
    const sid = Number(id);
    const cur = formData.store_ids || [];
    if (cur.includes(sid)) {
      setFormData({ ...formData, store_ids: cur.filter((x) => x !== sid) });
    } else {
      setFormData({ ...formData, store_ids: [...cur, sid] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const updateData = {
          email: formData.email,
          role: formData.role,
          first_name: formData.first_name,
          last_name: formData.last_name,
          phone: formData.phone,
          categories: formData.allowed_categories || [],
          store_ids: formData.store_ids || []
        };
        if (formData.password) updateData.password = formData.password;
        if (POS_PIN_ROLES.includes(formData.role)) {
          if (formData.pos_pin_clear) updateData.pos_pin_clear = true;
          else if (formData.pos_pin && String(formData.pos_pin).trim()) updateData.pos_pin = formData.pos_pin.trim();
        }
        await api.put(`/users/${editingUser.id}`, updateData);
        alert('User updated successfully');
      } else {
        if (!formData.username || !formData.password) {
          alert('Username and password are required for new users');
          return;
        }
        if (
          POS_PIN_ROLES.includes(formData.role) &&
          formData.pos_pin &&
          !/^\d{4}$/.test(String(formData.pos_pin).trim())
        ) {
          alert('POS PIN must be exactly 4 digits (or leave blank to set later)');
          return;
        }
        const payload = {
          username: formData.username,
          email: formData.email,
          password: formData.password,
          role: formData.role,
          first_name: formData.first_name,
          last_name: formData.last_name,
          phone: formData.phone
        };
        if (POS_PIN_ROLES.includes(formData.role) && formData.pos_pin && /^\d{4}$/.test(String(formData.pos_pin).trim())) {
          payload.pos_pin = String(formData.pos_pin).trim();
        }
        const { data } = await api.post('/auth/register', payload);
        const newId = data.userId;
        if (newId && formData.store_ids?.length) {
          await api.put(`/users/${newId}`, { store_ids: formData.store_ids });
        }
        alert('User created successfully');
      }

      resetForm();
      fetchUsers();
    } catch (error) {
      alert(error.response?.data?.error || `Error ${editingUser ? 'updating' : 'creating'} user`);
    }
  };

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (filterRole && u.role !== filterRole) return false;
      if (!q) return true;
      const blob = [u.username, u.email, u.full_name, u.first_name, u.last_name, u.phone, u.role]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return blob.includes(q);
    });
  }, [users, search, filterRole]);

  return (
    <div className="users-page users-page--cx">
      <PageNavbar />
      <div className="container users-cx-container">
        <header className="users-cx-header">
          <div>
            <h1 className="users-cx-title">User &amp; role management</h1>
            <p className="users-cx-sub">
              CakeXpress roles, outlet assignment, category visibility, and cashier POS PINs — one control surface
              for the brand.
            </p>
          </div>
          <button
            type="button"
            className="btn btn-primary users-cx-primary"
            onClick={() => {
              if (showForm) resetForm();
              else setShowForm(true);
            }}
          >
            {showForm ? 'Close form' : 'Add user'}
          </button>
        </header>

        <section className="users-pos-devices users-admin-whatsapp">
          <div className="users-pos-devices__head">
            <div>
              <h2>Admin WhatsApp</h2>
              <p>
                Number used for POS day-close summaries and other admin alerts. Save here to override the server
                env default.
              </p>
            </div>
          </div>
          <form className="users-admin-whatsapp__form" onSubmit={saveAdminWhatsApp}>
            <label>
              WhatsApp number
              <input
                className="form-control"
                type="tel"
                value={adminWaPhone}
                onChange={(e) => setAdminWaPhone(e.target.value)}
                placeholder="e.g. 919059406666 or 9059406666"
                required
              />
            </label>
            <button type="submit" className="btn btn-primary" disabled={adminWaSaving}>
              {adminWaSaving ? 'Saving…' : 'Save WhatsApp number'}
            </button>
            {adminWaSource ? (
              <span className="users-admin-whatsapp__hint">
                Source: {adminWaSource === 'saved' ? 'saved in app' : adminWaSource === 'env' ? 'server env' : 'not set'}
              </span>
            ) : null}
          </form>
        </section>

        <section className="users-pos-devices users-store-management">
          <div className="users-pos-devices__head">
            <div>
              <h2>Stores</h2>
              <p>Create, edit, or archive outlets. Use clear POS data before go-live for a fresh sales history.</p>
            </div>
          </div>
          <div className="users-store-reset-panel">
            <strong>Reset POS sales (production go-live)</strong>
            <p>Removes all POS sales, line items, POS expenses, and pending receipt print jobs.</p>
            <div className="users-store-reset-row">
              <input
                className="form-control"
                value={clearPosPhrase}
                onChange={(e) => setClearPosPhrase(e.target.value)}
                placeholder='Type CLEAR POS SALES to confirm'
              />
              <button type="button" className="btn btn-secondary" onClick={clearAllPosData} disabled={clearPosBusy}>
                {clearPosBusy ? 'Clearing…' : 'Clear all POS data'}
              </button>
            </div>
          </div>
          <form className="users-store-form" onSubmit={createStore}>
            <label>
              Store code
              <input
                className="form-control"
                value={storeForm.code}
                onChange={(e) => setStoreForm((form) => ({ ...form, code: e.target.value.toUpperCase() }))}
                placeholder="e.g. HYD-BNJ"
                maxLength={40}
                required
              />
            </label>
            <label>
              Store name
              <input
                className="form-control"
                value={storeForm.name}
                onChange={(e) => setStoreForm((form) => ({ ...form, name: e.target.value }))}
                placeholder="e.g. Banjara Hills"
                required
              />
            </label>
            <label>
              City
              <input
                className="form-control"
                value={storeForm.city}
                onChange={(e) => setStoreForm((form) => ({ ...form, city: e.target.value }))}
              />
            </label>
            <label>
              Phone
              <input
                className="form-control"
                type="tel"
                value={storeForm.phone}
                onChange={(e) => setStoreForm((form) => ({ ...form, phone: e.target.value }))}
                placeholder="Optional"
              />
            </label>
            <label className="users-store-form__address">
              Address
              <textarea
                className="form-control"
                rows={3}
                value={storeForm.address}
                onChange={(e) => setStoreForm((form) => ({ ...form, address: e.target.value }))}
                placeholder="Complete store address"
                required
              />
            </label>
            <label>
              GSTIN (optional)
              <input
                className="form-control"
                value={storeForm.gstin}
                onChange={(e) =>
                  setStoreForm((form) => ({
                    ...form,
                    gstin: e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 15)
                  }))
                }
                placeholder="15-character GSTIN"
                minLength={15}
                maxLength={15}
              />
            </label>
            <button type="submit" className="btn btn-primary" disabled={storeSaving}>
              {storeSaving ? 'Creating…' : 'Create store'}
            </button>
          </form>
          {stores.length ? (
            <div className="users-store-list">
              {stores.map((store) => (
                <article key={store.id} className={`users-store-card${store.is_active ? '' : ' is-archived'}`}>
                  <div className="users-store-card__head">
                    <strong>{store.name}</strong>
                    {!store.is_active ? <span className="users-store-badge">Archived</span> : null}
                  </div>
                  <span>{store.code}{store.city ? ` · ${store.city}` : ''}</span>
                  {store.address ? <small>{store.address}</small> : null}
                  {store.gstin ? <small>GSTIN: {store.gstin}</small> : null}
                  <small>
                    POS sales: {store.pos_sales_count ?? 0} · POS desktops: {store.pos_device_count ?? 0}
                  </small>
                  <div className="users-store-card__actions">
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => openStoreEdit(store)}>
                      Edit
                    </button>
                    {store.is_active ? (
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => deactivateStore(store)}>
                        Archive
                      </button>
                    ) : (
                      <>
                        <button type="button" className="btn btn-primary btn-sm" onClick={() => reactivateStore(store)}>
                          Restore
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => permanentlyDeleteStore(store)}
                        >
                          Delete permanently
                        </button>
                      </>
                    )}
                  </div>
                </article>
              ))}
            </div>
          ) : null}
        </section>

        {editingStore ? (
          <div className="users-bakery-modal-overlay" role="presentation" onClick={closeStoreEdit}>
            <div
              className="users-bakery-modal-shell users-store-edit-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="users-store-edit-title"
              onClick={(e) => e.stopPropagation()}
            >
              <button type="button" className="users-bakery-modal-close" onClick={closeStoreEdit} aria-label="Close">
                ×
              </button>
              <div className="users-bakery-form">
                <div className="users-bakery-form__hero">
                  <p className="users-bakery-form__eyebrow">Store</p>
                  <h2 id="users-store-edit-title" className="users-bakery-form__title">
                    Edit {editingStore.name}
                  </h2>
                  <p className="users-bakery-form__lede">Code: {editingStore.code} (cannot be changed)</p>
                </div>
                <form className="users-bakery-form__body" onSubmit={saveStoreEdit}>
                  <div className="users-bakery-grid2">
                    <label className="users-bakery-field">
                      <span>Store name</span>
                      <input
                        className="users-bakery-input"
                        value={storeEditForm.name}
                        onChange={(e) => setStoreEditForm((f) => ({ ...f, name: e.target.value }))}
                        required
                      />
                    </label>
                    <label className="users-bakery-field">
                      <span>City</span>
                      <input
                        className="users-bakery-input"
                        value={storeEditForm.city}
                        onChange={(e) => setStoreEditForm((f) => ({ ...f, city: e.target.value }))}
                      />
                    </label>
                    <label className="users-bakery-field users-bakery-field--wide">
                      <span>Address</span>
                      <textarea
                        className="users-bakery-input"
                        rows={3}
                        value={storeEditForm.address}
                        onChange={(e) => setStoreEditForm((f) => ({ ...f, address: e.target.value }))}
                      />
                    </label>
                    <label className="users-bakery-field">
                      <span>Phone</span>
                      <input
                        className="users-bakery-input"
                        value={storeEditForm.phone}
                        onChange={(e) => setStoreEditForm((f) => ({ ...f, phone: e.target.value }))}
                      />
                    </label>
                    <label className="users-bakery-field">
                      <span>GSTIN (optional)</span>
                      <input
                        className="users-bakery-input"
                        value={storeEditForm.gstin}
                        onChange={(e) =>
                          setStoreEditForm((f) => ({
                            ...f,
                            gstin: e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 15)
                          }))
                        }
                        maxLength={15}
                      />
                    </label>
                    <label className="users-bakery-field users-bakery-field--wide">
                      <span className="users-bakery-clear-pin">
                        <input
                          type="checkbox"
                          checked={storeEditForm.is_active}
                          onChange={(e) => setStoreEditForm((f) => ({ ...f, is_active: e.target.checked }))}
                        />
                        Active store (shown in POS and staff pickers)
                      </span>
                    </label>
                  </div>
                  <div className="users-bakery-actions">
                    <button type="submit" className="users-bakery-submit" disabled={storeSaving}>
                      {storeSaving ? 'Saving…' : 'Save store'}
                    </button>
                    <button type="button" className="users-bakery-cancel" onClick={closeStoreEdit}>
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        ) : null}

        <section className="users-pos-devices">
          <div className="users-pos-devices__head">
            <div>
              <h2>Store POS access links</h2>
              <p>
                Generate a complex reusable link and shared 4-digit PIN. The link can be opened in multiple browsers
                and remains active until you revoke it.
              </p>
            </div>
            <button type="button" className="btn btn-secondary" onClick={loadPosDevices} disabled={posDeviceLoading}>
              {posDeviceLoading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>

          <form className="users-pos-enroll-form" onSubmit={generatePosEnrollment}>
            <label>
              Store
              <select
                className="form-control"
                value={posDeviceForm.storeId}
                onChange={(e) => setPosDeviceForm((form) => ({ ...form, storeId: e.target.value }))}
                required
              >
                <option value="">Choose store…</option>
                {stores.map((store) => (
                  <option key={store.id} value={store.id}>{store.name} ({store.code})</option>
                ))}
              </select>
            </label>
            <label>
              Link name
              <input
                className="form-control"
                value={posDeviceForm.deviceName}
                onChange={(e) => setPosDeviceForm((form) => ({ ...form, deviceName: e.target.value }))}
                placeholder="e.g. Store 1 POS access"
              />
            </label>
            <label>
              POS PIN
              <input
                className="form-control"
                type="password"
                inputMode="numeric"
                pattern="\d{4}"
                maxLength={4}
                value={posDeviceForm.pin}
                onChange={(e) =>
                  setPosDeviceForm((form) => ({
                    ...form,
                    pin: e.target.value.replace(/\D/g, '').slice(0, 4)
                  }))
                }
                placeholder="4 digits"
                required
              />
            </label>
            <label>
              Confirm PIN
              <input
                className="form-control"
                type="password"
                inputMode="numeric"
                pattern="\d{4}"
                maxLength={4}
                value={posDeviceForm.confirmPin}
                onChange={(e) =>
                  setPosDeviceForm((form) => ({
                    ...form,
                    confirmPin: e.target.value.replace(/\D/g, '').slice(0, 4)
                  }))
                }
                placeholder="Repeat PIN"
                required
              />
            </label>
            <button type="submit" className="btn btn-primary">Generate POS link</button>
          </form>

          {generatedPosLink ? (
            <div className="users-pos-generated-link">
              <strong>Share this reusable link and PIN with the store</strong>
              <div>
                <input readOnly value={generatedPosLink} aria-label="Generated POS enrollment link" />
                <button type="button" className="btn btn-primary" onClick={copyPosEnrollment}>Copy link</button>
              </div>
              {generatedPosPin ? <p><strong>POS PIN: {generatedPosPin}</strong></p> : null}
              <small>The PIN is shown only now. Store it securely.</small>
            </div>
          ) : null}

          <div className="users-pos-device-grid">
            <div>
              <h3>Enrolled desktops</h3>
              {!posDevices.length ? <p>No POS desktops enrolled yet.</p> : null}
              {posDevices.map((device) => (
                <div key={device.id} className={`users-pos-device-row${device.revoked_at ? ' is-revoked' : ''}`}>
                  <div>
                    <strong>{device.device_name || `${device.store_name} POS`}</strong>
                    <span>{device.store_name} · Last seen: {device.last_seen_at ? formatDateTimeDisplay(device.last_seen_at) : 'Never'}</span>
                  </div>
                  {device.revoked_at ? (
                    <span className="users-pos-device-status">Revoked</span>
                  ) : (
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => revokePosDevice(device.id)}>
                      Revoke
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div>
              <h3>Reusable links</h3>
              {!posEnrollments.filter((item) => !item.revoked_at).length ? <p>No active links.</p> : null}
              {posEnrollments.filter((item) => !item.revoked_at).map((item) => (
                <div key={item.id} className="users-pos-device-row">
                  <div>
                    <strong>{item.device_name || `${item.store_name} POS`}</strong>
                    <span>{item.store_name} · Browsers: {item.active_devices || 0} · Active until revoked</span>
                  </div>
                  <div className="users-pos-device-actions">
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => resetPosEnrollmentPin(item.id, item.store_name)}
                    >
                      Reset PIN
                    </button>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => revokePosEnrollment(item.id)}>
                      Revoke link + browsers
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <div className="users-bakery-toolbar">
          <div className="users-cx-toolbar-row">
            <label className="users-cx-field">
              <span>Search</span>
              <input
                type="search"
                className="form-control"
                placeholder="Name, email, username, phone…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>
            <label className="users-cx-field">
              <span>Role filter</span>
              <select className="form-control" value={filterRole} onChange={(e) => setFilterRole(e.target.value)}>
                <option value="">All roles</option>
                {ROLE_ORDER.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_META[r]?.label || r}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="users-cx-legend">
            {ROLE_ORDER.map((r) => (
              <div key={r} className="users-cx-legend-item">
                <span className={roleBadgeClass(r)}>{ROLE_META[r]?.label || r}</span>
                <span className="users-cx-legend-desc">{ROLE_META[r]?.short}</span>
              </div>
            ))}
          </div>
        </div>

        {showForm
          ? createPortal(
          <div className="users-bakery-modal-overlay" role="presentation" onClick={resetForm}>
            <div
              className="users-bakery-modal-shell"
              role="dialog"
              aria-modal="true"
              aria-labelledby="users-bakery-form-title"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="users-bakery-modal-close"
                onClick={resetForm}
                aria-label="Close user form"
              >
                ×
              </button>
              <form
                className="users-bakery-form"
                aria-labelledby="users-bakery-form-title"
                onSubmit={handleSubmit}
              >
            <div className="users-bakery-form__accent-bar" aria-hidden />
            <header className="users-bakery-form__hero">
              <p className="users-bakery-form__eyebrow">
                {editingUser ? 'Update this team member' : 'Bake a new login into the brand'}
              </p>
              <h2 id="users-bakery-form-title" className="users-bakery-form__title">
                {editingUser ? 'Edit user' : 'Create user'}
              </h2>
              <p className="users-bakery-form__lede">
                Work top to bottom: who they are, how they access the system, which shop they belong to, then which
                order lanes they may peek into.
              </p>
              <div className="users-bakery-actions users-bakery-actions--top">
                <button type="submit" className="users-bakery-submit">
                  {editingUser ? 'Save changes' : 'Create user'}
                </button>
                {editingUser ? (
                  <button type="button" className="users-bakery-cancel" onClick={resetForm}>
                    Cancel
                  </button>
                ) : null}
              </div>
            </header>

              <div className="users-bakery-form__scroll">
              <section className="users-bakery-tier">
                <header className="users-bakery-tier__head">
                  <span className="users-bakery-tier__icon" aria-hidden>
                    ◆
                  </span>
                  <div>
                    <h3 className="users-bakery-tier__title">Personal details</h3>
                    <p className="users-bakery-tier__sub">Name and contact — shown on comms and receipts where relevant.</p>
                  </div>
                </header>
                <div className="users-bakery-grid2">
                  <div className="users-bakery-field">
                    <label htmlFor="ub-first">First name</label>
                    <input
                      id="ub-first"
                      type="text"
                      className="users-bakery-input"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="users-bakery-field">
                    <label htmlFor="ub-last">Last name</label>
                    <input
                      id="ub-last"
                      type="text"
                      className="users-bakery-input"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="users-bakery-field">
                    <label htmlFor="ub-phone">Phone / WhatsApp</label>
                    <input
                      id="ub-phone"
                      type="tel"
                      className="users-bakery-input"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                    />
                    <p className="users-bakery-field-hint">User contact only — not the day-close admin alert number.</p>
                  </div>
                  <div className="users-bakery-field">
                    <label htmlFor="ub-email">Email</label>
                    <input
                      id="ub-email"
                      type="email"
                      className="users-bakery-input"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>
                </div>
              </section>

              <section className="users-bakery-tier users-bakery-tier--charcoal">
                <header className="users-bakery-tier__head">
                  <span className="users-bakery-tier__icon" aria-hidden>
                    ◆
                  </span>
                  <div>
                    <h3 className="users-bakery-tier__title">Account &amp; role</h3>
                    <p className="users-bakery-tier__sub">Credentials for the full login, then their place in the team.</p>
                  </div>
                </header>

                {!editingUser && (
                  <div className="users-bakery-grid2">
                    <div className="users-bakery-field">
                      <label htmlFor="ub-user">Username</label>
                      <input
                        id="ub-user"
                        type="text"
                        className="users-bakery-input"
                        autoComplete="username"
                        value={formData.username}
                        onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                        required
                      />
                    </div>
                    <div className="users-bakery-field">
                      <label htmlFor="ub-pass">Password</label>
                      <input
                        id="ub-pass"
                        type="password"
                        className="users-bakery-input"
                        autoComplete="new-password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                )}

                {editingUser && (
                  <div className="users-bakery-field users-bakery-field--wide">
                    <label htmlFor="ub-pass2">New password (optional)</label>
                    <input
                      id="ub-pass2"
                      type="password"
                      className="users-bakery-input"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="Leave blank to keep the current password"
                    />
                  </div>
                )}

                <div className="users-bakery-field users-bakery-field--wide users-bakery-field--tight-top">
                  <label htmlFor="ub-role">Role</label>
                  <select
                    id="ub-role"
                    className="users-bakery-input users-bakery-select"
                    value={formData.role}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        role: e.target.value,
                        pos_pin: '',
                        pos_pin_clear: false
                      })
                    }
                    required
                  >
                    {ROLE_ORDER.map((r) => (
                      <option key={r} value={r}>
                        {ROLE_META[r]?.label || r} — {ROLE_META[r]?.short}
                      </option>
                    ))}
                  </select>
                  <p className="users-bakery-hint users-bakery-hint--gold">{ROLE_META[formData.role]?.description}</p>
                </div>
              </section>

              {ROLES_NEED_OUTLETS.includes(formData.role) && (
                <section className="users-bakery-tier users-bakery-tier--vanilla">
                  <header className="users-bakery-tier__head">
                    <span className="users-bakery-tier__icon" aria-hidden>
                      ◆
                    </span>
                    <div>
                      <h3 className="users-bakery-tier__title">Outlets &amp; counter</h3>
                      <p className="users-bakery-tier__sub">
                        Tick every shop this person may ring sales for. Cashiers and franchise leads need at least one.
                      </p>
                    </div>
                  </header>
                  {stores.length === 0 ? (
                    <p className="users-bakery-warn">
                      No active outlets found — add stores under <strong>POS → Stores</strong> first.
                    </p>
                  ) : (
                    <div className="users-bakery-outlet-grid" role="group" aria-label="Assigned outlets">
                      {stores.map((s) => (
                        <label key={s.id} className="users-bakery-outlet">
                          <input
                            type="checkbox"
                            checked={(formData.store_ids || []).includes(s.id)}
                            onChange={() => toggleStore(s.id)}
                          />
                          <span className="users-bakery-outlet__body">
                            <span className="users-bakery-outlet__name">{s.name}</span>
                            <span className="users-bakery-outlet__code">{s.code}</span>
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </section>
              )}

              {POS_PIN_ROLES.includes(formData.role) && (
                <section className="users-bakery-tier users-bakery-tier--vanilla users-bakery-tier--pin">
                  <header className="users-bakery-tier__head">
                    <span className="users-bakery-tier__icon" aria-hidden>
                      ◆
                    </span>
                    <div>
                      <h3 className="users-bakery-tier__title">POS quick code</h3>
                      <p className="users-bakery-tier__sub">
                        Four digits for PIN-only login at the terminal (Admin &amp; Accountant use staff login). Each PIN
                        must be unique. Optional until you are ready.
                      </p>
                    </div>
                  </header>
                  <div className="users-bakery-pin-row">
                    <div className="users-bakery-field users-bakery-field--pin">
                      <label htmlFor="ub-pin">{editingUser ? 'Set new PIN' : 'PIN (optional)'}</label>
                      <input
                        id="ub-pin"
                        type="password"
                        inputMode="numeric"
                        maxLength={4}
                        className="users-bakery-input users-bakery-input--pin"
                        placeholder="••••"
                        value={formData.pos_pin}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            pos_pin: e.target.value.replace(/\D/g, '').slice(0, 4),
                            pos_pin_clear: false
                          })
                        }
                      />
                    </div>
                    {editingUser ? (
                      <label className="users-bakery-clear-pin">
                        <input
                          type="checkbox"
                          checked={formData.pos_pin_clear}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              pos_pin_clear: e.target.checked,
                              pos_pin: e.target.checked ? '' : formData.pos_pin
                            })
                          }
                        />
                        <span>Remove PIN so they must use the full password until you set a new one.</span>
                      </label>
                    ) : null}
                  </div>
                </section>
              )}

              <section className="users-bakery-tier">
                <header className="users-bakery-tier__head">
                  <span className="users-bakery-tier__icon" aria-hidden>
                    ◆
                  </span>
                  <div>
                    <h3 className="users-bakery-tier__title">Order lanes (visibility)</h3>
                    <p className="users-bakery-tier__sub">
                      Leave every chip <em>off</em> for full visibility (typical for HQ). Turn chips <em>on</em> to limit
                      which pipelines appear in their Orders screens.
                    </p>
                  </div>
                </header>
                <div className="users-bakery-pills" role="group" aria-label="Order category filters">
                  {ORDER_CATEGORY_OPTIONS.map((opt) => (
                    <label key={opt.value} className="users-bakery-pill">
                      <input
                        type="checkbox"
                        checked={(formData.allowed_categories || []).includes(opt.value)}
                        onChange={() => toggleCategory(opt.value)}
                      />
                      <span>{opt.label}</span>
                    </label>
                  ))}
                </div>
              </section>
              </div>

              <footer className="users-bakery-actions users-bakery-actions--bottom">
                <button type="submit" className="users-bakery-submit">
                  {editingUser ? 'Save changes' : 'Create user'}
                </button>
                {editingUser && (
                  <button type="button" className="users-bakery-cancel" onClick={resetForm}>
                    Cancel edit
                  </button>
                )}
              </footer>
              </form>
            </div>
          </div>,
          document.body
          )
        : null}

        {loading ? (
          <div className="users-bakery-loading">Whisking up the team list…</div>
        ) : (
          <div className="users-bakery-directory">
            <h2 className="users-cx-table-title">Directory ({filteredUsers.length})</h2>
            <div className="users-cx-table-wrap">
              <table className="table users-cx-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Contact</th>
                    <th>Username</th>
                    <th>Role</th>
                    <th>Outlets</th>
                    <th>PIN</th>
                    <th>Created</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.id}>
                      <td>{user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || '—'}</td>
                      <td>
                        <div className="users-cx-contact">
                          <span>{user.email}</span>
                          {user.phone ? <small>{user.phone}</small> : null}
                        </div>
                      </td>
                      <td>{user.username}</td>
                      <td>
                        <span className={roleBadgeClass(user.role)}>{user.role}</span>
                      </td>
                      <td>
                        {user.assigned_stores?.length ? (
                          <ul className="users-cx-outlet-list">
                            {user.assigned_stores.map((s) => (
                              <li key={s.id}>
                                {s.name} <small>({s.code})</small>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <span className="users-cx-muted">—</span>
                        )}
                      </td>
                      <td>
                        {POS_PIN_ROLES.includes(user.role) ? (
                          <span className={user.has_pos_pin ? 'users-cx-pill users-cx-pill--ok' : 'users-cx-pill'}>
                            {user.has_pos_pin ? 'PIN on' : 'No PIN'}
                          </span>
                        ) : (
                          <span className="users-cx-muted">—</span>
                        )}
                      </td>
                      <td>{user.created_at ? formatDateDisplay(user.created_at) : '—'}</td>
                      <td>
                        <button type="button" className="btn btn-sm btn-primary" onClick={() => handleEdit(user)}>
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Users;
